import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  UsersThree, Stack, Package, CheckCircle, ClockCounterClockwise,
  Warning, TrendUp, ShieldCheck, Crown, ArrowRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_LABEL = {
  awaiting_sample: "Awaiting sample submission",
  awaiting_approval: "Awaiting approval",
  approved: "Approved",
  ordered: "Ordered",
  shipped: "Shipped",
  in_transit: "In transit",
  delivered: "Delivered",
};

const STATUS_COLOR = {
  awaiting_sample: "bg-rose-300",
  awaiting_approval: "bg-amber-200",
  approved: "bg-blue-200",
  ordered: "bg-indigo-300",
  shipped: "bg-purple-300",
  in_transit: "bg-cyan-300",
  delivered: "bg-emerald-400",
};

const ROLE_LABEL = {
  admin: "Admin",
  project_manager: "Project Manager",
  contractor: "Contractor",
};

function Tile({ icon, label, value, hint, accent = "primary", testid }) {
  const accentBg = {
    primary: "bg-[#007BFF]/10 text-[#007BFF]",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    red: "bg-red-50 text-red-600",
    zinc: "bg-zinc-100 text-zinc-600",
  }[accent];
  return (
    <div className="ctrl-card p-6" data-testid={testid}>
      <div className="flex items-start justify-between mb-4">
        <span className="text-xs font-medium text-zinc-500">{label}</span>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accentBg}`}>
          {icon}
        </div>
      </div>
      <div className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#001E44]">{value}</div>
      {hint && <div className="text-xs text-zinc-500 mt-2">{hint}</div>}
    </div>
  );
}

export default function AdminBusinessDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/dashboard-stats");
        setData(data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Failed to load stats");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading || !data) {
    return (
      <div className="space-y-6" data-testid="admin-dashboard-loading">
        <div className="h-10 w-1/3 bg-zinc-100 rounded animate-pulse" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={`skel-tile-${i}`} className="ctrl-card h-32 animate-pulse" />
          ))}
        </div>
        <div className="ctrl-card h-64 animate-pulse" />
      </div>
    );
  }

  const { totals, items_by_status, daily_signups, top_projects, recent_users } = data;
  const totalStatusItems = Object.values(items_by_status || {}).reduce((a, b) => a + b, 0) || 1;
  const peakSignups = Math.max(1, ...daily_signups.map((d) => d.count));
  const today = new Date().toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });

  return (
    <div className="space-y-10" data-testid="admin-business-dashboard">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="label-eyebrow text-[#007BFF] inline-flex items-center gap-2">
            <Crown size={12} weight="fill" /> Business overview · {today}
          </span>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#001E44] mt-2">
            Welcome back, {user?.name?.split(" ")[0] || "Admin"}.
          </h1>
          <p className="text-zinc-500 mt-2 text-sm sm:text-base">
            How <span className="font-semibold text-[#001E44]">liveprocure.com</span> is performing across every customer.
          </p>
        </div>
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 bg-[#007BFF] hover:bg-[#0062cc] text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-all shadow-sm hover:shadow-md"
          data-testid="admin-portal-link"
        >
          Admin Portal <ArrowRight size={14} weight="bold" />
        </Link>
      </header>

      {/* Headline KPIs */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Tile
          icon={<UsersThree size={18} weight="bold" />}
          label="Active users"
          value={totals.users}
          hint={`${totals.project_managers} PMs · ${totals.contractors} contractors`}
          accent="primary"
          testid="kpi-users"
        />
        <Tile
          icon={<Stack size={18} weight="bold" />}
          label="Projects"
          value={totals.projects}
          hint={`${totals.active_projects} currently active`}
          accent="emerald"
          testid="kpi-projects"
        />
        <Tile
          icon={<Package size={18} weight="bold" />}
          label="Items in flight"
          value={totals.items_in_flight}
          hint={`${totals.items} total tracked`}
          accent="amber"
          testid="kpi-in-flight"
        />
        <Tile
          icon={<TrendUp size={18} weight="bold" />}
          label="Delivered (30d)"
          value={totals.items_delivered_30d}
          hint={
            totals.items_overdue > 0
              ? `${totals.items_overdue} overdue across customers`
              : "No overdue items right now"
          }
          accent={totals.items_overdue > 0 ? "red" : "emerald"}
          testid="kpi-delivered"
        />
      </section>

      {/* Secondary KPIs */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Tile
          icon={<ShieldCheck size={18} weight="bold" />}
          label="Pending signup requests"
          value={totals.pending_signup_requests}
          hint={totals.pending_signup_requests > 0 ? "Action needed — review in Admin Portal" : "All clear"}
          accent={totals.pending_signup_requests > 0 ? "amber" : "emerald"}
          testid="kpi-pending"
        />
        <Tile
          icon={<TrendUp size={18} weight="bold" />}
          label="New requests (14d)"
          value={totals.new_signup_requests_14d}
          hint={`${totals.approved_signups_14d} approved in last 14 days`}
          accent="primary"
          testid="kpi-velocity"
        />
        <Tile
          icon={<Warning size={18} weight="bold" />}
          label="Critical-path items"
          value={totals.items_overdue}
          hint="Across every customer project"
          accent={totals.items_overdue > 0 ? "red" : "zinc"}
          testid="kpi-critical"
        />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Items by status — horizontal bars */}
        <div className="ctrl-card p-6 lg:col-span-2" data-testid="items-by-status">
          <div className="flex items-center justify-between mb-5">
            <div>
              <span className="label-eyebrow text-[#007BFF]">Pipeline</span>
              <h2 className="font-display text-xl font-semibold tracking-tight text-[#001E44] mt-1">
                Items by status
              </h2>
            </div>
            <span className="text-xs text-zinc-400">{totals.items} total</span>
          </div>
          {totals.items === 0 ? (
            <p className="text-sm text-zinc-500 italic">No procurement items yet. Once your customers start tracking items, the breakdown shows here.</p>
          ) : (
            <div className="space-y-3">
              {Object.entries(STATUS_LABEL).map(([key, label]) => {
                const count = items_by_status[key] || 0;
                const pct = (count / totalStatusItems) * 100;
                return (
                  <div key={key} className="flex items-center gap-3">
                    <div className="w-32 shrink-0 text-xs text-zinc-600">{label}</div>
                    <div className="flex-1 h-2.5 bg-zinc-100 rounded-full overflow-hidden">
                      <div className={`h-full ${STATUS_COLOR[key]} rounded-full transition-all`} style={{ width: `${pct}%` }} />
                    </div>
                    <div className="w-10 shrink-0 text-right text-xs font-semibold text-[#001E44]">{count}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* New signups (14d sparkline) */}
        <div className="ctrl-card p-6" data-testid="signup-trend">
          <div className="mb-5">
            <span className="label-eyebrow text-[#007BFF]">Growth</span>
            <h2 className="font-display text-xl font-semibold tracking-tight text-[#001E44] mt-1">
              New users · last 14 days
            </h2>
          </div>
          <div className="flex items-end gap-1 h-32">
            {daily_signups.map((d) => {
              const h = (d.count / peakSignups) * 100;
              return (
                <div
                  key={d.date}
                  className="flex-1 bg-[#007BFF]/15 hover:bg-[#007BFF]/30 transition-colors rounded-t-sm relative group min-h-[2px]"
                  style={{ height: `${Math.max(h, 2)}%` }}
                  title={`${d.date}: ${d.count}`}
                >
                  {d.count > 0 && (
                    <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] font-semibold text-[#001E44] opacity-0 group-hover:opacity-100 transition-opacity">
                      {d.count}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-2 text-[10px] text-zinc-400">
            <span>{daily_signups[0]?.date.slice(5)}</span>
            <span>Today</span>
          </div>
        </div>
      </section>

      {/* Top projects + Recent signups */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="ctrl-card p-6" data-testid="top-projects-card">
          <div className="mb-5">
            <span className="label-eyebrow text-[#007BFF]">Engagement</span>
            <h2 className="font-display text-xl font-semibold tracking-tight text-[#001E44] mt-1">
              Top projects by activity
            </h2>
          </div>
          {top_projects.length === 0 ? (
            <p className="text-sm text-zinc-500 italic">No project activity yet.</p>
          ) : (
            <ul className="space-y-2.5">
              {top_projects.map((p, i) => (
                <li key={p.project_id} className="flex items-center gap-3 py-2 border-b border-zinc-100 last:border-0" data-testid={`top-project-${p.project_id}`}>
                  <div className="w-7 h-7 rounded-lg bg-zinc-100 text-zinc-600 font-display font-semibold text-xs flex items-center justify-center shrink-0">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#001E44] text-sm truncate">{p.name}</div>
                  </div>
                  <span className="chip bg-[#007BFF]/10 text-[#007BFF]">
                    <Package size={11} weight="bold" /> {p.items}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="ctrl-card p-6" data-testid="recent-users-card">
          <div className="flex items-center justify-between mb-5">
            <div>
              <span className="label-eyebrow text-[#007BFF]">People</span>
              <h2 className="font-display text-xl font-semibold tracking-tight text-[#001E44] mt-1">
                Latest signups
              </h2>
            </div>
            <Link to="/admin" className="text-xs font-semibold text-[#007BFF] hover:underline" data-testid="recent-users-link">
              View all →
            </Link>
          </div>
          {recent_users.length === 0 ? (
            <p className="text-sm text-zinc-500 italic">No users yet.</p>
          ) : (
            <ul className="space-y-2">
              {recent_users.map((u) => (
                <li key={u.id} className="flex items-center gap-3 py-2 border-b border-zinc-100 last:border-0" data-testid={`recent-user-${u.id}`}>
                  <div className="w-8 h-8 bg-gradient-to-br from-[#001E44] to-[#1E407C] text-white rounded-full flex items-center justify-center text-xs font-semibold shrink-0">
                    {u.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#001E44] text-sm truncate">{u.name}</div>
                    <div className="text-[11px] text-zinc-500 truncate">{u.email}</div>
                  </div>
                  <span className="chip bg-zinc-100 text-zinc-700">{ROLE_LABEL[u.role] || u.role}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
